import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, Pencil } from "lucide-react";
import type { Profile } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { Input } from "@web-shared/components/ui/input";
import { accessToken, refreshSession } from "@web-shared/lib/account";
import { json, webApi } from "@web-shared/lib/web-api";
import { profilePath } from "@/lib/ctas";
import { Avatar } from "./Avatar";

/** Longest side of a stored avatar. Anything bigger is shrunk in the browser
 * first, so a phone photo uploads as ~40 KB, not 4 MB. */
const AVATAR_PX = 512;

/** Edit profile: picture, name, handle, bio. */
export function EditProfileButton({ profile, iconOnly }: { profile: Profile; iconOnly?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {iconOnly ? (
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg border border-strong" onClick={() => setOpen(true)} title="Edit profile" aria-label="Edit profile">
          <Pencil className="size-4" />
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          Edit profile
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md">
          <DialogTitle>Edit profile</DialogTitle>
          <DialogDescription className="sr-only">Your picture, name, handle and bio.</DialogDescription>
          {open && <EditProfileForm profile={profile} onDone={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditProfileForm({ profile, onDone }: { profile: Profile; onDone: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(profile.displayName ?? "");
  const [handle, setHandle] = useState(profile.handle);
  const [bio, setBio] = useState(profile.bio ?? "");
  const [avatar, setAvatar] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement | null>(null);

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      const blob = await shrink(f);
      setAvatar({ blob, url: URL.createObjectURL(blob) });
    } catch {
      setError("That image couldn't be read. Try a JPEG, PNG or WebP.");
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (avatar) {
        const res = await fetchAvatar(avatar.blob);
        if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? "Upload failed.");
      }
      const changes: Record<string, string | null> = {};
      if (displayName.trim() !== (profile.displayName ?? "")) changes.displayName = displayName.trim() || null;
      if (bio.trim() !== (profile.bio ?? "")) changes.bio = bio.trim() || null;
      const nextHandle = handle.trim().replace(/^@/, "").toLowerCase();
      if (nextHandle !== profile.handle) changes.handle = nextHandle;
      if (Object.keys(changes).length > 0) await webApi("/me", json("PATCH", changes));
      // The session carries the handle the shell links to; take the new one.
      if (changes.handle) await refreshSession();
      await queryClient.invalidateQueries({ queryKey: ["web"] });
      onDone();
      if (changes.handle) navigate(profilePath(nextHandle), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => file.current?.click()}
          className="group relative cursor-pointer rounded-full"
          aria-label="Change picture"
        >
          <Avatar name={profile.username} url={avatar?.url ?? profile.avatarUrl} className="size-16 text-lg" />
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
            <Camera className="size-5" />
          </span>
        </button>
        <input
          ref={file}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => void pick(e.target.files?.[0])}
        />
        <p className="text-xs text-muted-foreground">A square picture works best.</p>
      </div>
      <label className="block space-y-1 text-xs text-muted-foreground">
        Name
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={profile.username} maxLength={60} />
      </label>
      <label className="block space-y-1 text-xs text-muted-foreground">
        Handle
        <Input value={handle} onChange={(e) => setHandle(e.target.value)} maxLength={31} autoCapitalize="none" spellCheck={false} />
      </label>
      <label className="block space-y-1 text-xs text-muted-foreground">
        Bio
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={300}
          rows={3}
          className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="What you make, what you're after"
        />
      </label>
      {error && <p className="text-xs text-removed">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy && <Loader2 className="animate-spin" />}
          Save
        </Button>
      </div>
    </form>
  );
}

/** PUT the image itself: the body is the bytes, not JSON. */
async function fetchAvatar(blob: Blob): Promise<Response> {
  return fetch("/api/me/avatar", {
    method: "PUT",
    headers: { "Content-Type": blob.type, Authorization: `Bearer ${accessToken()}` },
    body: blob,
  });
}

/** Centre-crop to a square and scale to AVATAR_PX, as WebP. */
async function shrink(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const size = Math.min(AVATAR_PX, side);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No canvas.");
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.85));
  if (!blob) throw new Error("Could not encode.");
  return blob;
}
